import {
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid, gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../api/axiosClient';
import CustomChip from '../../components/mui/chip';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import QuestionToolbar from './QuestionToolbar';

const TableQuestion = () => {
  const [loading, setLoading] = useState(true)
  const [reload, setReload] = useState(0)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('desc')
  const [pageSize, setPageSize] = useState(10)
  const [list, setList] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('qid')
  const [selected, setSelected] = useState([])

  const loadQuestions = 
    async (sort, query, column) => {
      try {
        const response = await apiService.get('/questions', {
          params: {
            query,
            sort,
            column,
            page,
            pageSize
          }
        });
        const payload = response.data;
        setTotal(payload.size);
        setList(payload.list);
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    }

  useEffect(() => {
    loadQuestions(sort, searchValue, sortColumn)
  }, [page, pageSize, reload])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadQuestions(newModel[0].sort, searchValue, newModel[0].field)
    } else {
      setSort('desc')
      setSortColumn('qid')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadQuestions(sort, value, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      flex: 0.2,
      minWidth: 120,
      field: 'username',
      headerName: '고객ID',
      renderCell: params => (
        <Link to={`/admin/question/${params.row.qid}`} >
          <CustomChip
            size='small'
            skin='light'
            color='primary'
            label={params.row.qid}
            sx={{ '& .MuiChip-label': { textTransform: 'capitalize' }, padding: 2, cursor: 'pointer' }}
          />
        </Link>
      )
    },
    {
      flex: 0.2,
      minWidth: 110,
      field: 'title',
      headerName: '문의유형',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.title}
        </Typography>
      )
    },
    {
      flex: 0.2,
      field: 'descript',
      minWidth: 80,
      headerName: '문의내용',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.descript}
        </Typography>
      )
    },
    {
      flex: 0.1,
      field: 'reply',
      minWidth: 140,
      headerName: '회신여부',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.reply}
        </Typography>
      )
    },
    {
      flex: 0.2,
      field: 'uptime',
      minWidth: 140,
      headerName: '변경날짜',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.uptime.toLocaleString('ko-KR')}
        </Typography>
      )
    }
  ]

  return (
    loading ?
    <Spinner/>
    :
    <Card>
      <CardHeader title='문의내역' />
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.qid}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: QuestionToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.qid),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Card>
  )
}

export default TableQuestion
